<script setup>
import { Loader } from "@googlemaps/js-api-loader"
const props = defineProps({
  currentLocation: Number,
  nearby: Array,
  locations: Object,
  mapContent: String,
  sectionTitle: String
})

const locationData = {
  current: props.currentLocation,
  locations: props.locations,
  nearby: props.nearby
}

const MAP_INIT_ROOT_MARGIN = '600px 0px';
const mapInitStarted = ref(false);
const mapWrapRef = ref(null);
const mapCanvasRef = ref(null);
let mapViewportObserver = null;

const runWhenMapNearViewport = (initMap) => {
  if (mapInitStarted.value) return;

  const mapContainer = mapWrapRef.value;
  if (!mapContainer || !('IntersectionObserver' in window)) {
    mapInitStarted.value = true;
    void initMap();
    return;
  }

  mapViewportObserver = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    mapViewportObserver?.disconnect();
    mapViewportObserver = null;
    mapInitStarted.value = true;
    void initMap();
  }, { rootMargin: MAP_INIT_ROOT_MARGIN });

  mapViewportObserver.observe(mapContainer);
};

const initializeMap = async () => {
  const { key } = await $fetch('/api/maps-key');
  const loader = new Loader({
    apiKey: key,
    version: "weekly",
    libraries: ["marker"]
  });

  const map = {
    data: locationData,
    wrap: mapWrapRef.value,
    el: mapCanvasRef.value,
    gm: null, // the google map instance ( google.maps.Map class )
    init() {
      if (!map.el) return;
      map.gm = new google.maps.Map(map.el, map.options());
      // initialize dependencies
      [data, markers, infoWindow, nearby, events].forEach(dep => dep.init());
    },
    reset: false,
    loaded() {
      return Boolean(document.getElementById('nlm-gm-js-api'));
    },
    options() {
      return {
        center: idToLatLng(map.data.current),
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        mapId: 'location-map',
        zoomControlOptions: {
          position: google.maps.ControlPosition.TOP_RIGHT
        },
        // styles: [{
        //     featureType: "road.highway",
        //     elementType: "geometry",
        //     stylers: [{ color: "#8EA7C3" }],
        //   },
        //   {
        //     featureType: "road.highway",
        //     elementType: "geometry.stroke",
        //     stylers: [{ visibility: "off" }],
        //   },
        //   {
        //     featureType: "water",
        //     elementType: "geometry",
        //     stylers: [{ color: "#70D3E7" }],            
        //   },
        //   {
        //     featureType: "water",
        //     elementType: "labels.text.fill",
        //     stylers: [{ color: "#185abc" }],
        //   }]
      }
    }
  };

  loader.importLibrary('maps').then(async () => {
    map.init()
  })

  const data = (() => {
    const closest = [];

    function setLatLng(location) {
      location.LatLng = new google.maps.LatLng(location.latitude, location.longitude);
    }

    function setDistance(location) {
      // location.gmDistance = google.maps.geometry.spherical.computeDistanceBetween(currentLocation().LatLng, location.LatLng) / 1609.34;
      location.gmDistance = location.distance;
    }

    function updateClosest() {
      closest.sort((a,b) => locationById(a).gmDistance - locationById(b).gmDistance);
      if (closest.length > 3) closest.pop();
    }

    

    return {
      init() {
        setLatLng(currentLocation());
        Object.keys(map.data.locations).forEach(id => {
          if (isCurrent(id)) return;
          let location = map.data.locations[id];
          setLatLng(location);          
          setDistance(location)
          closest.push(id);
          updateClosest();
        });
        map.data.nearby = closest.map(id => parseInt(id, 10));
        
      }
    }
  })();

  function isCurrent(id) {
    return map.data.current == id;
  }

  function locationById(id) {
    return map.data.locations[id];
  }

  function locationType(id) {
    return isCurrent(id)
      ? 'current'
      : isNearby(id)
      ? 'nearby'
      : 'default';
  }

  function currentLocation() {
    return map.data.locations[map.data.current];
  }

  function idToLatLng(id) {
    let location = locationById(id);
    return new google.maps.LatLng(location ? location.latitude : 0, location ? location.longitude : 0);
  }

  function isNearby(id) {
    return map.data.nearby.indexOf(parseInt(id, 10)) > -1;
  }

  function resetMapCenter() {
    map.gm.panTo(locationById(map.data.current).LatLng);
  }

  function scrollTop() {
    return window.pageYOffset || document.documentElement.scrollTop;
  }


  const markers = (() => {
    const _refs = {};
    const iconPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#07937b" stroke="#07937b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ><path d="${iconPath}"/></svg>`;
    const icons = {
      current: {
        anchor: { x: 12, y: 16 },
        color: '#07937b',
        scale: 2.5,
        zIndex: 100,
      },
      default: {
        anchor: { x: 12.5, y: 24 },
        color: "#9ac92f",
        scale: 2,
        zIndex: 1,
      },
      nearby: {
        anchor: { x: 12, y: 16 },
        color: ['#12b0ea', '#b812ea', '#ea7612' ],
        scale: 2.5,
        zIndex: 99,
      }
    };

    let nearbyIndex = 0;

    function create(id) {
        const type = locationType(id);
        let icon = icons[type];
        
        const location = locationById(id);    
        google.maps.importLibrary("marker").then((marker) => {

          let color = icon.color;
          if (type === 'nearby') {
            color = icon.color[nearbyIndex];                      
          }

          _refs[id] = new marker.AdvancedMarkerElement({
            // animation: google.maps.Animation.DROP,            
            map: map.gm,
            content: buildContent(color),
            position: idToLatLng(id),
            title: locationById(id).pulseName,            
            zIndex: icon.zIndex
          });          
          _refs[id].addListener('click', clicked(id));

          if (isNearby(id)) {
            nearbyIndex++;
          }

          if (!isCurrent(id)) {
            const content = _refs[id].content;

            content.style.opacity = "0";
            content.addEventListener("animationend", (event) => {
              content.classList.remove("drop");
              content.style.opacity = "1";
            });

            const time = 1 + Math.random(); // 2s delay for easy to see the animation

            content.style.setProperty("--delay-time", time + "s");
            intersectionObserver.observe(content);
          }
        });
        
    }

    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add("drop");
          intersectionObserver.unobserve(entry.target);
        }
      }
    });

    function buildContent(color) {
      const content = document.createElement("div");

      content.classList.add("location-marker");
      content.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="56" height="64" fill="${color}" stroke="#ffffff" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" ><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
      return content;
    }

    function exists(id) {
      return _refs.hasOwnProperty(id);
    }

    function setVisibility(id, visible) {
      if (_refs[id]) {
        _refs[id].visibility = visible;
      }
      
    }

    function setZIndex(id, zIndex) {
      if (_refs[id]) {
        _refs[id].zIndex = zIndex;
      }      
    }

    function clicked(id) {
      return e => infoWindow.show({id});
    }

    function update() {
      const gmLatLngBounds = map.gm.getBounds();
      Object.keys(_refs).forEach(id => {
        const gmLatLng = idToLatLng(id);
        const isWithinBounds = gmLatLngBounds.contains(gmLatLng);
        setVisibility(id, isWithinBounds);
      });
    }

    function updateNearbyMarkers(isNearbyActive) {
      let icon = isNearbyActive ? icons.nearby : icons.default;
      map.data.nearby.forEach((id, index) => {
        let map = _refs[id].map;
        _refs[id].setMap(null);
        _refs[id].icon.anchor = icon.anchor;
        _refs[id].icon.fillColor = isNearbyActive ? icons.nearby.color[index] : icon.color;
        _refs[id].icon.scale = icon.scale;
        _refs[id].icon.zIndex = icon.zIndex;
        _refs[id].setMap(map);
      });
    }

    return {
      init() {
        Object.keys(map.data.locations).forEach(async (id) => await create(id));
        [...map.data.nearby].reverse().forEach((id, index) => setZIndex(id, index + 2));
        setZIndex(map.data.current, 99);
        setVisibility(map.data.current, true);
      },
      show(ids = [], condition = null) {
        ids.forEach(id => {
          if (!condition || condition(id)) {
            if (!exists(id)) create(id);
            setVisibility(id, true);
          }
        })
      },
      hide(ids = []) {
        ids.forEach(id => setVisibility(id, false));
      },
      hideAll() {
        Object.keys(_refs).forEach(id => setVisibility(id, false));
      },
      update,
      updateNearbyMarkers
    }
  })();

  const infoWindow = (() => {
    let active = null;
    let pendingUpdate = 0;

    function content(id) {
      const location = locationById(id);
      const locType = locationType(id);
      let locationTitle = locationNameClass(id);
      if (locType === 'nearby') { locationTitle += nearby.isActive() ? "" : "--uniform"; }
      return `<div class="map-info-window ${locType}">
        <address>
          <a class="${locationTitle}" href="${location.link}">${location.pulseName}</a>
          <br>
          ${location.addressArea} ${location.postalCode}
        </address>
      </div>`;
    }

    function locationNameClass(id) {
      const type = locationType(id);
      return type == 'nearby' ? type + '-' + (map.data.nearby.indexOf(parseInt(id, 10)) + 1) : type;
    }

    function create(id) {
      return new google.maps.InfoWindow({
        content: content(id),
        position: idToLatLng(id),
        pixelOffset: new google.maps.Size(0, -48),
        id: id
      });
    }

    function setActive(id = 0) {
      active = id ? create(id) : null;
    }

    function showActive() {
      if (active) active.open({ map: map.gm, shouldFocus: false });
    }

    function hideActive() {
      if (active) active.close();
    }

    function recreateActive() {
      if (active) {
        hideActive();
        setActive(active.id);
        showActive();
      }
    }

    function open(id) {
      hideActive();
      setActive(id);
      showActive();
      map.gm.panTo(idToLatLng(id));
      map.gm.panBy(0, -100);
    }

    function reset() {
      const id = map.data.current;
      open(id);
    }

    return {
      init() {
        setTimeout(reset, 1000);
      },
      reset() {
        const id = map.data.current;
        pendingUpdate = id;
        setTimeout(() => {
          if (pendingUpdate == id) open(id);
        }, 100);
      },
      show({ id = 0 }) {
        if (id) {
          pendingUpdate = id;
          setTimeout(() => {
            if (pendingUpdate == id) open(id);
          }, 100);
        }
      },
      recreateActive
    }
  })();

  const nearby = (() => {
    const wrap = document.querySelector('.nearest-locations');
    const toggle = document.getElementById('toggle-nl');
    const card = {
      touched: false,
      els: [],
      selector: '.nl-cards>.nl-card',
      listeners: {
        mouseenter({ target: cardEl }) {
          activate(cardEl);
        },
        mouseleave({ target: cardEl }) {
          if (!card.touched) deactivate(cardEl, true);
        },
        touchstart({ target: touchEl }) {
          const cardEl = cardElFromTouch(touchEl);
          card.touched = true;
          deactivate(activeCard());
          activate(cardEl);
        }
      }
    };

    function isActive() {
      return wrap.classList.contains('open');
    }

    function toggleNearby() {
      isActive() ? close() : open();
      markers.updateNearbyMarkers(isActive());
    }

    function open() {
      const gmLatLngBounds = new google.maps.LatLngBounds();
      // normalizeScroll();
      setCardEls();
      createCardContent();
      wrap.classList.add('open');
      resetMapCenter();
      [map.data.current, ...map.data.nearby].forEach(id => {
        const gmLatLng = idToLatLng(id);
        gmLatLngBounds.extend(gmLatLng);
      });
      map.gm.fitBounds(gmLatLngBounds, mapPadding());
      map.gm.panToBounds(gmLatLngBounds);
      cardEventListeners('add');
      //infoWindow.recreateActive();
      infoWindow.reset();
    }

    function close() {
      cardEventListeners('remove');
      wrap.classList.remove('open');
      resetMapCenter();
      map.gm.setZoom(map.options().zoom);
      deactivate(activeCard(), true);
    }

    function setCardEls() {
      if (!card.els.length) card.els = wrap.querySelectorAll(card.selector);
    }

    function createCardContent() {
      map.data.nearby.forEach((id, index) => {
        let location = locationById(id);        
        let el = card.els.item(index);
        el.setAttribute('data-location-id', id);
        el.innerHTML = cardInnerHtml(location)
      })
    }

    function cardInnerHtml(location) { 
      const statusVariant = location.statusName.replace(/\s+/g, '-').toLowerCase();
      // Placeholder image added temporary
      const locationImage = location.listing_image_thumbnail ? location.listing_image_thumbnail.url : 'https://placehold.co/600x400';
      return ` 
        <div class="nl-area"> 
          <div class="nl-info-area"> 
            <div class="nl-image-area"> 
              <img class="nl-thumbnail mw-100 rounded-4" src="${locationImage}" loading="lazy" decoding="async"/> 
            </div> 
            <div class="nl-detail"> 
              <h6 class="nl-name">${location.pulseName}</h6> 
              <div class="location-status location-status--${statusVariant} location-status--no-dot">${location.statusName}</div> 
              <small>${parseFloat(location.gmDistance).toFixed(2)} miles away</small> 
            </div> 
          </div> 
        </div> 
        <div class="nl-area nl-link-area"> 
          <a class="nl-link button btn btn-outline-secondary" href="${location.link}">Learn More</a> 
        </div>`; 
    } 

    function mapPadding() {
      return window.innerWidth < 987
      ? { top: 200, right: 50, bottom: 10, left: 50 }
      : { top: 300, right: 50, bottom: 50, left: 50 };
    }

    function supportsScrollTo() {
      return 'scrollBehavior' in document.documentElement.style;
    }

    const offset = () => {      
      if (window.innerWidth < 987) {
        return 400;
      }
      else if (window.innerWidth > 992 && window.innerWidth < 1200){
        return -200;
      }
      return -100;
    }

    function normalizeScroll() {
      if (!supportsScrollTo()) return;
      const { bottom } = map.wrap.getBoundingClientRect();
      
      const scrollTopTo = scrollTop() - (window.innerHeight - bottom) + offset() ;
      window.scrollTo({
        top: scrollTopTo,
        behavior: 'smooth',
      });
    }

    function cardEventListeners(addOrRemove) {
      card.els.forEach(cardEl => {
        Object.keys(card.listeners).forEach(eventName => {
          cardEl[`${addOrRemove}EventListener`](eventName, card.listeners[eventName], false);
        });
      });
    }

    function activeCard() {
      return wrap.querySelector('.nl-active');
    }

    function cardElFromTouch(touchEl) {
      let el = touchEl;
      while (el.parentElement && !el.getAttribute('data-location-id')) {
        el = el.parentElement;
      }
      return el.getAttribute('data-location-id') ? el : null;
    } 

    function activate(cardEl) {
      const id = cardEl.getAttribute('data-location-id');
      infoWindow.show({id});
      cardEl.classList.add('nl-active');
    }

    function deactivate(cardEl, reset = false) {
      if (cardEl) cardEl.classList.remove('nl-active');
      if (reset) infoWindow.reset();
    }

    return {
      init() {
        if (wrap && toggle) toggle.addEventListener('click', toggleNearby);
      },
      isActive,
    }
  })();

  const events = (() => {

    const listeners = {
      bounds_changed: {
        fn: markers.update,
        active: false
      },
      zoom_changed: {
        fn: markers.update,
        active: false
      }
    };

    function throttleHandler(eventName) {
      return () => {
        if (!listeners[eventName].active) {
          listeners[eventName].active = setTimeout(e => {
            listeners[eventName].fn(e);
            listeners[eventName].active = false;
          }, 200);
        }
      }
    }
    
    return {
      init() {
        Object.keys(listeners).forEach(eventName => {
          map.gm.addListener(eventName, throttleHandler(eventName));
        });
      }
    }
  })();


  
}

onMounted(() => {
  runWhenMapNearViewport(initializeMap);
});

onBeforeUnmount(() => {
  mapViewportObserver?.disconnect();
  mapViewportObserver = null;
});
</script>

<template>
  <section >
    <div id="nearby-locations-wrap" class="block location-map container" ref="mapWrapRef" >
      <div class="text-center my-7">
        <h3>{{ sectionTitle }}</h3>
      </div>
      <div id="nearby-locations-map" ref="mapCanvasRef"></div>
      <div class="nearest-locations col float-end">
        <button class="nl-button d-flex align-items-center justify-content-center py-2" id="toggle-nl">
            <span>Find nearby locations</span>
            <span class="icon-plus-circle1 ms-1 nl-button-icon"></span>
        </button>
        <div class="nl-cards">
          <div class="nl-card"></div>
          <div class="nl-card"></div>
          <div class="nl-card"></div>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="row">
        <div class="col mt-4" id="location-map" v-html="mapContent">
        </div>
      </div>
    </div>
  </section>
  
</template>

<style lang="scss">
.location-marker {
  top: 10px;
  position: relative;
}

@keyframes drop {
  0% {
    transform: translateY(-200px) scaleY(0.9);
    opacity: 0;
  }
  5% {
    opacity: 0.7;
  }
  50% {
    transform: translateY(0px) scaleY(1);
    opacity: 1;
  }
  65% {
    transform: translateY(-17px) scaleY(0.9);
    opacity: 1;
  }
  75% {
    transform: translateY(-22px) scaleY(0.9);
    opacity: 1;
  }
  100% {
    transform: translateY(0px) scaleY(1);
    opacity: 1;
  }
}
.drop {
  animation: drop 0.3s linear forwards var(--delay-time);
}


#nearby-locations-wrap {
  position: relative;

  .current-location {

    .cl {
      &-address {
        padding-right: 4rem;

        h2 {
          font-size: 2rem;
          color: #07937b;
          line-height: 1.25;
        }

        h3 {
          font-size: 1.25rem;
          line-height: 1.25;
        }

        h4 {
          font-size: 1rem;
        }

        &::after {
          content: "";
          display: block;
          width: 2px;
          height: 100%;
          background: #ddd;
          position: absolute;
          top: 0;
          right: 2rem;
        }

        @include media-breakpoint-down(sm) {
          padding-right: 0;

          &::after {
            display: none;
          }
        }
      }
    }
  }

  #nearby-locations-map {
    height: 350px;
    transition: height 1s ease;
    background: #f7f7f7;
    border-radius: 24px;

    @include media-breakpoint-up(md) {
      height: 730px;
    }
  }

  .map-info-window {
    font-family: Poppins, sans-serif;
    font-size: .9rem;

    a {
      color: #444;
      font-weight: bold;
      font-size: 1em;
      letter-spacing: normal;

      &.current {
        color: #07937b
      }

      &.nearby {

        &-1 {
          color: #b812ea;
        }

        &-2 {
          color: #ea7612;
        }

        &-3 {
          color: #12b0ea
        }
      }
    }

    &.default {
        font-size: .9rem
    }

    &.current a {
      color: #07937b
    }
  }

  .nearest-locations {
      position: relative;
      bottom: 80px;
      right: 20px;
      width: 250px;
      max-width: 1140px;      
      height: 60px;
      transition: all .3s ease;
      transition-property: height, bottom;

      &.open {
        height: 224px;
        border-radius: 27px;
        box-shadow: 0 7px 19px 0 rgba(0, 0, 0, 0.16);
        background: $color-secondary;
        transition: all .3s ease;
        transition-property: height, bottom;

        .nl-button-icon {
          transform: rotate(45deg);
        }

        .nl-cards {
          display: flex;  
          
          @include media-breakpoint-down(sm) {
            border-bottom-right-radius: 24px;          
            border-bottom-left-radius: 24px; 
            padding-top: 25px;
          }
        }

        .nl-card {
          cursor: pointer;
          transform: translate3d(0, 0, 0);
          border-radius: 12px;
          border-top: 12px solid;
        }

        

        @include media-breakpoint-up(lg) {
          width: 90%;
          bottom: 7px;
          position: absolute;

          .nl-button {            
            border: 0px;
            box-shadow: unset;
            max-width: 250px;            
          }
        }

        @include media-breakpoint-down(lg) {
          border-radius: unset;
        }

        @include media-breakpoint-down(sm) {          
          box-shadow: none;  
          .nl-button {
            border-radius: 0px;
          }               
        }
      }

      @include media-breakpoint-down(lg) {
        position: relative;
        width: 100%;
        bottom: 0;
        left: 0;
      }

      @include media-breakpoint-down(sm) {
        bottom: 58px;          
      }

      .nl {
        &-button {
          width: 100%;
          text-align: left;
          line-height: 24px;
          background: none;
          border: none;
          font-weight: bold;
          letter-spacing: normal;
          outline: none;
          border: solid 1px #d9d9d9;
          background: #fff;
          border-radius: 27px;
          box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.16);

          span {
            font-weight: 600;            
          }

          @include media-breakpoint-down(lg) {
            width: 100%;
            bottom: 0;
            border-radius: unset;
          }

          @include media-breakpoint-down(sm) {
            border-bottom-right-radius: 24px;          
            border-bottom-left-radius: 24px; 
            box-shadow: none;
          }
          
        }

        &-button-icon {
          height: 16px;
          width: 16px;
          border: 2px solid #333;
          display: inline-block;
          position: relative;
          vertical-align: middle;
          margin: -0.2rem 0 0 1rem;
          border-radius: 50%;
          transition: transform .4s ease;
          transform-origin: center;
          margin-top: 1px;
          @include media-breakpoint-down(md) {
            float: right;
          }

          &%button-icon-psuedo {
            content: "";
            display: block;
            height: 8px;
            width: 0.15rem;
            background: #333;
            position: absolute;
            right: 0;
            left: 0;
            margin: 0 auto;
            top: 50%;
          }

          &::before {
            @extend %button-icon-psuedo;
            transform: translateY(-50%);
          }

          &::after {
            @extend %button-icon-psuedo;
            transform: translateY(-50%) rotate(90deg);
          }
        }

        &-cards {
          display: none;
          flex-flow: row nowrap;
          height: 184px;
          padding-top: 1rem;
          overflow-x: auto;
          overflow-y: hidden;
          
          &::-webkit-scrollbar {
            display: none;
          }

          @include media-breakpoint-down(sm) {
            border: solid 1px #d9d9d9;          
          }
        }

        &-card {
          display: flex;
          flex-flow: column nowrap;
          flex-grow: 1;
          flex-basis: 300px;
          justify-content: space-between;
          position: relative;          
          min-width: 300px;
          height: 100%;
          margin: -.8rem .8rem .8rem;
          padding: 8px;
          border-radius: 4px;
          box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.16);          
          color: #333;
          transition: all ease-in-out .4s;
          animation: fadeInUp .3s cubic-bezier(.4,0,0,1) .4s forwards;
          transform: translate3d(0, 50%, 0);

          &:nth-child(1) {
            border-color: #b812ea;
          }
          
          &:nth-child(2) {
            border-color: #ea7612;
          }
      
          &:nth-child(3) {
            border-color: #12b0ea;
          }
      
          &.nl-active {
            margin-top: -1rem;
          }

          
          @include media-breakpoint-down(sm) {
            flex-basis: 250px;
            min-width: 250px;
          }
        }
        
        &-image-area {
          width: 50%;
          min-width: 50%;
          margin-right: .8rem;
        }
        &-info-area {
          display: flex;
          flex-flow: row nowrap;
        }
        &-link-area {
          align-self: flex-end;
        }

        &-thumbnail {
          width: 100%;
          border-radius: .25rem;
          margin-right: .8rem;
        }
        &-detail {
          display: flex;
          flex-flow: column nowrap;
          //flex-grow: 1;
          justify-content: center;
          flex: 1 1 50%;
        }
        &-name {
          margin: 0;
          color: #333;

          @include media-breakpoint-down(sm) {
            font-size: 14px;
          }
        }
        &-link {
          border-width: 1px;
          text-align: center;
          width: auto;
          padding: .33rem 1rem;
          height: auto;
          line-height: 1.4;
          font-size: .75rem;
          font-weight: bold;
        }
      }
    }
}


</style>